import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { ErrorMessage, Field, Form, Formik } from "formik";
import React, { useState } from "react";
import api from "../../../API/api";
import * as Yup from "yup";
import {
  Box,
  Modal,
  Typography,
  Switch,
  FormControlLabel,
  CircularProgress,
  Button,
} from "@mui/material";
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import { CloseOutlined } from "@mui/icons-material";
import { toast } from "sonner";
import colors from "assets/theme/base/colors";

const AddSiteModal = ({
  addSiteOpen,
  setAddSiteOpen,
  selectedSite,
  getAllSites,
}) => {
  const { white, gradients } = colors;
  const [suggestions, setSuggestions] = useState([]);
  const [siteLocation, setSiteLocation] = useState(null);
  const [apiError, setApiError] = useState(null);
  const [manualEntry, setManualEntry] = useState(false);
  const [loading, setLoading] = useState(false);

  const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 400,
    bgcolor: "background.paper",
    boxShadow: 24,
    borderRadius: "14px",
    outline: "none !important",
  };

  const validationSchema = Yup.object().shape({
    site_name: Yup.string().required("Site Name is required"),
    site_location: manualEntry
      ? Yup.string().required("Location Name is required")
      : Yup.string().required("Location is required"),
    description: Yup.string().required("Description is required"),
    latitude: manualEntry
      ? Yup.number()
          .required("Latitude is required")
          .typeError("Must be a number")
      : Yup.number().nullable(),
    longitude: manualEntry
      ? Yup.number()
          .required("Longitude is required")
          .typeError("Must be a number")
      : Yup.number().nullable(),
  });

  const initialValues = selectedSite
    ? {
        site_name: selectedSite?.site_name,
        site_location: selectedSite?.location,
        description: selectedSite?.description,
        latitude: selectedSite?.latitude || "",
        longitude: selectedSite?.longitude || "",
        site_id: selectedSite?.id,
      }
    : {
        site_name: "",
        site_location: "",
        description: "",
        latitude: "",
        longitude: "",
      };

  const handleSubmit = async (values, { setSubmitting }) => {
    setApiError(null);

    if (manualEntry && (!values.latitude || !values.longitude)) {
      toast.error("Latitude and Longitude are required");
      setSubmitting(false);
      return;
    }

    try {
      setLoading(true);
      const payload = {
        latitude: manualEntry ? values.latitude : siteLocation?.latitude || "",
        longitude: manualEntry
          ? values.longitude
          : siteLocation?.longitude || "",
        site_name: values.site_name,
        location: values.site_location,
        description: values.description,
      };

      if (selectedSite) {
        payload.site_id = selectedSite?.site_id || selectedSite?.id;
        const response = await api.put(`/cv/siteapi`, payload);
        toast.success(response.data.message);
      } else {
        const response = await api.post("/cv/siteapi", payload);
        toast.success(response.data.message);
      }

      setAddSiteOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.message || "Something went wrong!");
    } finally {
      setSubmitting(false);
      getAllSites();
      setLoading(false);
    }
  };

  const fetchLocations = async (input) => {
    if (!input || input.length < 3 || manualEntry) return;
    try {
      const response = await api.get(`/places?input=${input}`);
      if (response.data.status === "ok") {
        const locationSuggestions = response?.data?.data?.map((location) => ({
          label: location.place_name,
          value: location.place_id,
        }));
        setSuggestions(locationSuggestions);
      } else {
        console.error("Error fetching locations:", response.data.status);
      }
    } catch (error) {
      console.error("Error fetching locations:", error.message);
    }
  };

  const handleSuggestionClick = async (suggestion, setFieldValue) => {
    try {
      setSuggestions([]);
      const response = await api.get(
        `/places-details?place_id=${suggestion?.value}`
      );
      if (response.data.status === "ok") {
        const { latitude, longitude } = response?.data?.data;
        setSiteLocation({
          label: suggestion.label,
          value: suggestion.value,
          latitude,
          longitude,
        });
        setFieldValue("latitude", latitude);
        setFieldValue("longitude", longitude);
      } else {
        console.error("Error fetching place details:", response.data.status);
      }
    } catch (error) {
      console.error("Error fetching place details:", error.message);
    }
  };

  const handleInputChange = async (e) => {
    const input = e.target.value;
    fetchLocations(input);
  };

  return (
    <React.Fragment>
      <Modal
        open={addSiteOpen}
        onClose={() => setAddSiteOpen(false)}
        sx={{ padding: "0 !important" }}
      >
        <Box sx={style} className="add-site-modal">
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ isSubmitting, setFieldValue, values, dirty }) => (
              <Form>
                <Box
                  sx={{
                    mb: 3,
                    backgroundColor: gradients.info.main,
                    borderTopLeftRadius: "14px",
                    borderTopRightRadius: "14px",
                  }}
                >
                  <Box
                    sx={{
                      padding: "15px",
                      display: "flex",
                      justifyContent: "space-between",
                      color: white.main,
                    }}
                  >
                    <Typography sx={{ fontSize: "18px", fontWeight: "500" }}>
                      {selectedSite ? "EDIT SITE" : "ADD SITE"}
                    </Typography>
                    <CloseOutlined
                      onClick={() => setAddSiteOpen(false)}
                      sx={{ cursor: "pointer", fontSize: "30px", mt: "5px" }}
                    />
                  </Box>
                </Box>

                <Box sx={{ padding: "15px" }}>
                  <MDBox mb={2}>
                    <Field
                      name="site_name"
                      as={MDInput}
                      fullWidth
                      label="Site Name"
                    />
                    <ErrorMessage
                      name="site_name"
                      component="div"
                      className="text-danger"
                      style={{
                        color: "red",
                        fontSize: "12px",
                        fontWeight: "300",
                        marginLeft: "5px",
                      }}
                    />
                  </MDBox>

                  <MDBox mb={2}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={manualEntry}
                          onChange={() => {
                            setManualEntry(!manualEntry);
                            setFieldValue("site_location", "");
                            setFieldValue("latitude", "");
                            setFieldValue("longitude", "");
                            setSuggestions([]);
                          }}
                        />
                      }
                      label="Enter GPS Coordinates Manually"
                    />
                  </MDBox>

                  {manualEntry ? (
                    <>
                      <MDBox mb={2}>
                        <Field
                          name="site_location"
                          as={MDInput}
                          label="Location Name"
                          fullWidth
                        />
                        <ErrorMessage
                          name="site_location"
                          component="div"
                          className="text-danger"
                          style={{
                            color: "red",
                            fontSize: "12px",
                            fontWeight: "300",
                            marginLeft: "5px",
                          }}
                        />
                      </MDBox>
                      <MDBox mb={2}>
                        <Field
                          name="latitude"
                          as={MDInput}
                          label="Latitude"
                          fullWidth
                        />
                        <ErrorMessage
                          name="latitude"
                          component="div"
                          className="text-danger"
                          style={{
                            color: "red",
                            fontSize: "12px",
                            fontWeight: "300",
                            marginLeft: "5px",
                          }}
                        />
                      </MDBox>
                      <MDBox mb={2}>
                        <Field
                          name="longitude"
                          as={MDInput}
                          label="Longitude"
                          fullWidth
                        />
                        <ErrorMessage
                          name="longitude"
                          component="div"
                          className="text-danger"
                          style={{
                            color: "red",
                            fontSize: "12px",
                            fontWeight: "300",
                            marginLeft: "5px",
                          }}
                        />
                      </MDBox>
                    </>
                  ) : (
                    <MDBox mb={2}>
                      <Field
                        name="site_location"
                        type="text"
                        as={MDInput}
                        fullWidth
                        placeholder="Search for a location..."
                        onChange={(e) => {
                          setFieldValue("site_location", e.target.value);
                          handleInputChange(e);
                        }}
                        autoComplete="off"
                      />
                      {suggestions.length > 0 && (
                        <ul
                          style={{
                            listStyleType: "none",
                            padding: 0,
                            border: "1px solid #ddd",
                            marginTop: "5px",
                            maxHeight: "150px",
                            overflowY: "auto",
                          }}
                        >
                          {suggestions.map((suggestion, index) => (
                            <li
                              key={index}
                              onClick={() => {
                                setFieldValue(
                                  "site_location",
                                  suggestion.label
                                );
                                handleSuggestionClick(
                                  suggestion,
                                  setFieldValue
                                );
                              }}
                              style={{
                                padding: "10px",
                                cursor: "pointer",
                                backgroundColor: "#fff",
                                fontSize: "14px",
                              }}
                            >
                              {suggestion.label}
                            </li>
                          ))}
                        </ul>
                      )}
                      <ErrorMessage
                        name="site_location"
                        component="div"
                        className="text-danger"
                        style={{
                          color: "red",
                          fontSize: "12px",
                          fontWeight: "300",
                          marginLeft: "5px",
                        }}
                      />
                    </MDBox>
                  )}

                  <MDBox mb={2}>
                    <Field
                      name="description"
                      as={MDInput}
                      label="Description"
                      multiline
                      rows={3}
                      fullWidth
                      placeholder="Type here..."
                    />
                    <ErrorMessage
                      name="description"
                      component="div"
                      className="text-danger"
                      style={{
                        color: "red",
                        fontSize: "12px",
                        fontWeight: "300",
                        marginLeft: "5px",
                      }}
                    />
                  </MDBox>

                  {apiError && (
                    <MDTypography
                      variant="caption"
                      color="error"
                      style={{ marginBottom: "10px" }}
                    >
                      {apiError}
                    </MDTypography>
                  )}

                  <MDBox mt={4} mb={1}>
                    <Button
                      type="submit"
                      variant="gradient"
                      color="info"
                      fullWidth
                      disabled={loading || !dirty}
                      sx={{
                        width: "100%",
                        height: "38px",
                        fontSize: "14px",
                        fontWeight: "500",
                        background: !dirty
                          ? "#e0e0e0"
                          : `${gradients.info.state} !important`,
                        color: white.main,
                        border: "none",
                        marginRight: "0",
                        marginBottom: "0",
                        "&:hover": {
                          background: `${gradients.info.state} !important`,
                          color: white.main,
                        },
                      }}
                    >
                      {loading ? (
                        <CircularProgress
                          size={25}
                          sx={{
                            color: "white !important",
                          }}
                        />
                      ) : isSubmitting ? (
                        "Submitting..."
                      ) : selectedSite ? (
                        "Update"
                      ) : (
                        "Add"
                      )}
                    </Button>
                  </MDBox>
                </Box>
              </Form>
            )}
          </Formik>
        </Box>
      </Modal>
    </React.Fragment>
  );
};

export default AddSiteModal;
